import { useInfiniteQuery } from '@tanstack/react-query';
import { FeatureTypeEnum } from 'api/enums';
import { IFeature } from 'api/models';
import { getFeatureAnnouncements } from 'api/services';
import { Button } from 'library/buttons';
import { ArrowRightIcon, ChevronDownIcon, SearchIcon } from 'library/icons';
import { Skelton } from 'library/skelton';
import { NavTabPills } from 'library/tabs';
import {
    getFeatureCoverFile,
    hasFeatureGuideline,
    getFeatureDetailsUrl,
    getFeatureTypeLabel,
    sortFeaturesByDate,
} from 'modules/shared/features/release-announcements/feature-announcement.utils';
import FeatureMedia from 'modules/shared/features/release-announcements/components/FeatureMedia';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

const PAGE_SIZE = 20;

const styles: Record<FeatureTypeEnum, string> = {
    [FeatureTypeEnum.NEW]: 'bg-primary text-white',
    [FeatureTypeEnum.IMPROVED]: 'bg-success-100 text-success-600',
    [FeatureTypeEnum.FIXED]: 'bg-warning-100 text-warning-600',
};

const month = (date: string) =>
    new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' }).format(new Date(date));

const ReleaseAnnouncementsLoading = () => (
    <main className="w-full p-6 md:p-10" aria-label="Loading product updates" aria-busy="true">
        <div className="grid gap-10 lg:grid-cols-[180px_minmax(0,1fr)]">
            <aside className="hidden lg:block">
                <div className="space-y-4 px-3">
                    <Skelton width="72px" />
                    {Array.from({ length: 4 }, (_, index) => (
                        <div key={index} className="flex items-center justify-between gap-3">
                            <Skelton width="112px" />
                            <Skelton width="20px" />
                        </div>
                    ))}
                </div>
            </aside>
            <section className="min-w-0">
                <Skelton width="150px" className="text-3xl" />
                <Skelton width="340px" className="mt-2" />

                <div className="mt-8 flex flex-col gap-3 md:flex-row">
                    <Skelton variant="rectangular" height="44px" className="flex-1" />
                    <div className="flex items-center gap-2">
                        {[64, 72, 92, 68].map((width) => (
                            <Skelton key={width} variant="rectangular" width={`${width}px`} height="32px" />
                        ))}
                    </div>
                </div>

                <div className="mt-3 flex gap-2">
                    {[88, 76, 104].map((width) => (
                        <Skelton
                            key={width}
                            variant="rectangular"
                            width={`${width}px`}
                            height="32px"
                            className="rounded-full"
                        />
                    ))}
                </div>

                <div className="mb-3 mt-10 flex items-center gap-2">
                    <Skelton width="130px" className="text-lg" />
                    <Skelton width="70px" />
                </div>
                <div className="space-y-3">
                    {Array.from({ length: 3 }, (_, index) => (
                        <div key={index} className="rounded-xl bg-card-color p-5 shadow-sm ring-1 ring-black/[.03]">
                            <div className="flex items-center gap-2">
                                <Skelton variant="rectangular" width="58px" height="24px" className="rounded-full" />
                                <Skelton width="90px" />
                            </div>
                            <Skelton width={index === 1 ? '48%' : '62%'} className="mt-3 text-lg" />
                            <Skelton width={index === 2 ? '72%' : '88%'} className="mt-1" />
                        </div>
                    ))}
                </div>
            </section>
        </div>
    </main>
);

const ReleaseAnnouncementsPage = () => {
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [type, setType] = useState<FeatureTypeEnum | 'All'>('All');
    const [module, setModule] = useState('All');
    const open = params.get('open');
    const guideId = params.get('guide');
    const targetId = open;
    const releases = useInfiniteQuery(
        ['featureAnnouncements', 'archive'],
        ({ pageParam = 0 }) => getFeatureAnnouncements({ offset: pageParam, limit: PAGE_SIZE }),
        {
            getNextPageParam: (lastPage, pages) => {
                const loadedCount = pages.reduce((count, page) => count + page.items.length, 0);
                return loadedCount < lastPage.total ? loadedCount : undefined;
            },
            staleTime: 10 * 60 * 1000,
        }
    );

    const announcements = useMemo(() => releases.data?.pages.flatMap((page) => page.items) ?? [], [releases.data]);
    const features = useMemo(
        () => sortFeaturesByDate(announcements.flatMap((announcement) => announcement.features ?? [])),
        [announcements]
    );
    const modules = useMemo(() => ['All', ...new Set(features.map((feature) => feature.module))], [features]);
    const filtered = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();

        return features.filter(
            (feature) =>
                (type === 'All' || feature.type === type) &&
                (module === 'All' || feature.module === module) &&
                `${feature.title} ${feature.description} ${feature.module} ${feature.tags.join(' ')}`
                    .toLowerCase()
                    .includes(normalizedQuery)
        );
    }, [features, type, module, query]);
    const groups = useMemo(
        () =>
            Object.entries(
                filtered.reduce<Record<string, IFeature[]>>((result, feature) => {
                    const key = month(feature.date);
                    (result[key] ??= []).push(feature);
                    return result;
                }, {})
            ),
        [filtered]
    );

    useEffect(() => {
        if (
            targetId &&
            !features.some((feature) => feature._id === targetId) &&
            releases.hasNextPage &&
            !releases.isError &&
            !releases.isFetchingNextPage
        ) {
            void releases.fetchNextPage();
        }
    }, [features, targetId, releases]);

    if (releases.isLoading) {
        return <ReleaseAnnouncementsLoading />;
    }

    if (releases.isError && !releases.data) {
        return (
            <main className="w-full p-6 md:p-10">
                <div className="rounded-xl bg-card-color p-12 text-center shadow-sm">
                    <p className="text-grayscale-500">Unable to load product updates.</p>
                    <Button className="mx-auto mt-4" variant="shaded" size="sm" onClick={() => releases.refetch()}>
                        Try again
                    </Button>
                </div>
            </main>
        );
    }

    if (guideId) return <Navigate replace to={`/whats-new/features/${encodeURIComponent(guideId)}/guide`} />;

    return (
        <main className="w-full p-6 md:p-10">
            <div className="grid gap-10 lg:grid-cols-[180px_minmax(0,1fr)]">
                <aside className="hidden lg:block">
                    <nav className="sticky top-6 space-y-1">
                        <p className="px-3 pb-2 text-xs font-semibold tracking-wider text-grayscale-500">RELEASES</p>
                        {groups.map(([label, updates]) => (
                            <button
                                key={label}
                                onClick={() =>
                                    document.getElementById(`release-${label}`)?.scrollIntoView({ behavior: 'smooth' })
                                }
                                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm text-grayscale-500 hover:bg-grayscale-100 hover:text-primary"
                            >
                                <span>{label}</span>
                                <span className="text-xs">{updates.length}</span>
                            </button>
                        ))}
                    </nav>
                </aside>
                <section className="min-w-0">
                    <div>
                        <h3>What’s New</h3>
                        <p className="mt-2 text-grayscale-500">Every feature and update released to ProgressCenter.</p>
                    </div>
                    <div className="mt-8 flex flex-col gap-3 md:flex-row">
                        <label className="relative flex-1">
                            <SearchIcon className="absolute left-4 top-3 h-4 w-4 text-grayscale-500" />
                            <input
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder="Search updates"
                                className="h-11 w-full rounded-lg border border-separator-color-light bg-card-color pl-10 pr-3 text-sm shadow-sm"
                            />
                        </label>
                        <nav>
                            <NavTabPills
                                items={(['All', ...Object.values(FeatureTypeEnum)] as const).map((item) => ({
                                    content: item === 'All' ? item : getFeatureTypeLabel(item),
                                    value: item,
                                }))}
                                value={type}
                                onChange={(value) => setType(value as FeatureTypeEnum | 'All')}
                            />
                        </nav>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {modules.map((item) => (
                            <Button
                                key={item}
                                type="button"
                                size="xs"
                                color={module === item ? 'primary' : 'secondary'}
                                onClick={() => setModule(item)}
                                className="!rounded-full !shadow-none"
                            >
                                {item === 'All' ? 'All modules' : item}
                            </Button>
                        ))}
                    </div>
                    {!groups.length ? (
                        <div className="mt-12 rounded-xl bg-card-color p-12 text-center text-grayscale-500 shadow-sm">
                            No updates match these filters.{' '}
                            {(query || type !== 'All' || module !== 'All') && (
                                <Button
                                    className="mx-auto mt-4"
                                    variant="shaded"
                                    size="xs"
                                    onClick={() => {
                                        setQuery('');
                                        setType('All');
                                        setModule('All');
                                    }}
                                >
                                    Clear filters
                                </Button>
                            )}
                        </div>
                    ) : (
                        groups.map(([label, updates]) => (
                            <section id={`release-${label}`} key={label} className="mt-10 scroll-mt-6">
                                <div className="mb-3 flex items-baseline gap-2">
                                    <h2 className="text-lg font-semibold">{label}</h2>
                                    <span className="text-sm text-grayscale-500">{updates.length} updates</span>
                                </div>
                                <div className="space-y-3">
                                    {updates.map((feature) => {
                                        return (
                                            <article
                                                key={feature._id}
                                                className="overflow-hidden rounded-xl bg-card-color shadow-sm ring-1 ring-black/[.03]"
                                            >
                                                <button
                                                    className="flex w-full items-start gap-4 p-5 text-left"
                                                    aria-expanded={open === feature._id}
                                                    onClick={() => {
                                                        const next = open === feature._id ? null : feature._id;
                                                        setParams(next ? { open: next } : {});
                                                    }}
                                                >
                                                    <span className="min-w-0 flex-1">
                                                        <span className="flex items-center gap-2">
                                                            <span
                                                                className={`rounded-full px-2 py-1 text-xs font-semibold ${styles[feature.type]}`}
                                                            >
                                                                {getFeatureTypeLabel(feature.type)}
                                                            </span>
                                                            <span className="text-xs text-grayscale-500">
                                                                {feature.module}
                                                            </span>
                                                        </span>
                                                        <span className="mt-3 block text-[17px] font-semibold">
                                                            {feature.title}
                                                        </span>
                                                        <motion.span
                                                            initial={false}
                                                            animate={{
                                                                height: open === feature._id ? 'auto' : '1.25rem',
                                                            }}
                                                            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                                                            className={`mt-1 block overflow-hidden text-sm leading-5 text-grayscale-500 ${open === feature._id ? '' : 'truncate'}`}
                                                        >
                                                            {feature.description}
                                                        </motion.span>
                                                    </span>
                                                    <ChevronDownIcon
                                                        className={`mt-4 h-3 w-3 shrink-0 text-grayscale-500 transition-transform duration-300 ease-in-out ${open === feature._id ? 'rotate-180' : ''}`}
                                                    />
                                                </button>
                                                <AnimatePresence initial={false}>
                                                    {open === feature._id &&
                                                        (getFeatureCoverFile(feature) ||
                                                            feature.coverImageUrl ||
                                                            hasFeatureGuideline(feature)) && (
                                                            <motion.div
                                                                initial={{ height: 0, opacity: 0 }}
                                                                animate={{ height: 'auto', opacity: 1 }}
                                                                exit={{ height: 0, opacity: 0 }}
                                                                transition={{
                                                                    height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                                                                    opacity: { duration: 0.2 },
                                                                }}
                                                                className="overflow-hidden"
                                                            >
                                                                <div className="border-t border-separator-color-light px-5 pb-5">
                                                                    {(getFeatureCoverFile(feature) ||
                                                                        feature.coverImageUrl) && (
                                                                        <FeatureMedia
                                                                            feature={feature}
                                                                            className="mx-auto mt-5 h-auto w-auto max-h-[420px] max-w-full rounded-xl object-contain"
                                                                        />
                                                                    )}
                                                                    {hasFeatureGuideline(feature) && (
                                                                        <Button
                                                                            color="primary"
                                                                            size="xs"
                                                                            className="mt-4"
                                                                            onClick={() =>
                                                                                navigate(getFeatureDetailsUrl(feature))
                                                                            }
                                                                        >
                                                                            Know more{' '}
                                                                            <ArrowRightIcon className="h-3 w-3" />
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                </AnimatePresence>
                                            </article>
                                        );
                                    })}
                                </div>
                            </section>
                        ))
                    )}
                    {releases.hasNextPage && (
                        <div className="mt-8 text-center">
                            <Button
                                className="mx-auto"
                                color="secondary"
                                disabled={releases.isFetchingNextPage}
                                onClick={() => releases.fetchNextPage()}
                            >
                                {releases.isFetchingNextPage ? 'Loading…' : 'Load more'}
                            </Button>
                        </div>
                    )}
                    {releases.isError && releases.data && (
                        <p className="mt-4 text-center text-sm text-danger-500">Unable to load more updates.</p>
                    )}
                </section>
            </div>
        </main>
    );
};

export default ReleaseAnnouncementsPage;
